// ./src/addressAnswer.ts — an ADDRESS answer on the client (ROADMAP A2e #86).
//
// One custom question whose answer is a whole postal address: the six parts a person record has. The SERVER owns the
// rules (`eventsmagic-api` boundary/addressAnswer.ts — it refuses an address without a street, a city and a country,
// stores the parts + one formatted line, and writes all six onto a linked person). This file is the client's half:
// the value shape, a PRESENCE check so the form can point at the empty part before it is sent, and the mapping from a
// Google place pick to the six parts.
// ⓘ Pure (no React), so both apps and their guards import it.

/** The six parts, in reading order — the person record's own address columns. */
export const ADDRESS_PARTS = ["streetAddress", "extraAddress", "city", "stateProvince", "postalCode", "country"] as const;

export type AddressPart = (typeof ADDRESS_PARTS)[number];

/** An address answer as the form holds it: every part a string ('' = blank). */
export type AnswerAddress = Record<AddressPart, string>;

/** The wire shape (`PublicAnswerAddress` / `PublicAnswerAddressInput`): a blank part is null. */
export type AnswerAddressInput = Record<AddressPart, string | null>;

export const EMPTY_ADDRESS: AnswerAddress = {
  streetAddress: "",
  extraAddress: "",
  city: "",
  stateProvince: "",
  postalCode: "",
  country: "",
};

/** What each part is called on screen. */
export const ADDRESS_PART_LABEL: Record<AddressPart, string> = {
  streetAddress: "Street address",
  extraAddress: "Apartment, suite, etc.",
  city: "City",
  stateProvince: "State / province",
  postalCode: "Postal / ZIP code",
  country: "Country",
};

/**
 * The parts an address must have to be one. ⚠ A PRESENCE check — the server refuses the same answer whatever the client
 * sends; this only lets the form point at the empty box instead of printing the server's sentence in the footer.
 */
export const ADDRESS_REQUIRED_PARTS: readonly AddressPart[] = ["streetAddress", "city", "country"];

/** Any value → an AnswerAddress (a stored answer, a wire value, a half-built object). Never throws. */
export const toAnswerAddress = (raw: unknown): AnswerAddress => {
  const src = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const out = { ...EMPTY_ADDRESS };

  for (const key of ADDRESS_PARTS) {
    out[key] = typeof src[key] === "string" ? (src[key] as string) : "";
  }

  return out;
};

/** Is this an address value (vs a string / number / option list)? */
export const isAnswerAddress = (v: unknown): v is AnswerAddress =>
  !!v && typeof v === "object" && !Array.isArray(v) && ADDRESS_PARTS.some((key) => key in (v as object));

export const addressIsEmpty = (v: unknown): boolean => {
  const addr = toAnswerAddress(v);

  return ADDRESS_PARTS.every((key) => !addr[key].trim());
};

/** The required parts a NON-blank address is missing ([] = complete, and [] for a blank one too). */
export const addressMissingParts = (v: unknown): AddressPart[] => {
  if (addressIsEmpty(v)) {
    return [];
  }

  const addr = toAnswerAddress(v);

  return ADDRESS_REQUIRED_PARTS.filter((key) => !addr[key].trim());
};

/** "Enter the city and country — or leave the address blank." */
export const addressMissingMessage = (missing: readonly AddressPart[]): string => {
  const names = missing.map((key) => ADDRESS_PART_LABEL[key].toLowerCase());
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0] ?? "";

  return `Enter the ${list} — or leave the address blank.`;
};

/** The value for the wire: trimmed parts, a blank one as null; null when the whole address is blank. */
export const toAddressInput = (v: unknown): AnswerAddressInput | null => {
  if (addressIsEmpty(v)) {
    return null;
  }

  const addr = toAnswerAddress(v);
  const out = {} as AnswerAddressInput;

  for (const key of ADDRESS_PARTS) {
    out[key] = addr[key].trim() || null;
  }

  return out;
};

/** The fields of a Google place pick this reads (`@em/ui` AddressData) — structural, so this file needs no UI import. */
export interface PlacePickLike {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

/**
 * A place picked from the autocomplete → the six parts, the way the contact editor maps it (region and country arrive
 * as short codes: "ON", "CA"). ⚠ The pick REPLACES the address — a part the pick does not have is cleared, except the
 * apartment line, which Google rarely knows and the guest typed themselves.
 */
export const addressFromPlace = (pick: PlacePickLike, current: AnswerAddress, typedStreet: string): AnswerAddress => ({
  streetAddress: (pick.address1 || typedStreet || "").trim(),
  extraAddress: (pick.address2 || current.extraAddress || "").trim(),
  city: (pick.city || "").trim(),
  stateProvince: (pick.region || "").trim(),
  postalCode: (pick.postalCode || "").trim(),
  country: (pick.country || "").trim(),
});

/**
 * The message a form shows AT an address question that was started but not finished, or null — for any question (a
 * non-address one is never "incomplete"). Every public form runs it next to its required check, so the guest is told
 * which part is missing before the server refuses the whole submission with the same rule.
 */
export const incompleteAddressMessage = (question: { questionType?: string | null }, value: unknown): string | null => {
  if (question.questionType !== "address") {
    return null;
  }

  const missing = addressMissingParts(value);

  return missing.length > 0 ? addressMissingMessage(missing) : null;
};
